// Build-from-Documents batch upload modal (s98). Sequential states:
//   1) upload   — multi-file drop zone / picker
//   2) processing — each file runs through the fan-out pipeline one at a time
//   3) summary  — detected type + section count per file; Populate All (5A) OR Review & Choose
//   4) review   — per-document stepper with section + per-row checkboxes (5B)
// Populate (either path) writes the fan-out sections, then homes each document into
// Important Documents. Orchestration lives in @/lib/batch-upload (unit-tested); this owns
// UI + the real network deps. Design: build-from-documents.md.

import { useMemo, useRef, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useSingleFlight } from "@/hooks/useSingleFlight";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle, ArrowLeft, ArrowRight, Check, CheckCircle2, FileText, Loader2, UploadCloud } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { DOCUMENT_ACCEPT, DOCUMENT_ACCEPT_LABEL } from "@/lib/upload-accept";
import type { Client, GenericCategory, GenericSection } from "@shared/schema";
import { CATEGORY_SLUGS } from "@shared/constants/categories";
import { SECTION_SLUGS } from "@shared/constants/sections";
import type { FanOutSection } from "@/components/FanOutBanner";
import { FanOutSectionChecklist } from "@/components/FanOutSectionChecklist";
import {
  runBatchProcessing,
  collectPopulateTargets,
  collectSelectedPopulateTargets,
  initBatchSelection,
  documentTypeLabel,
  totalSections,
  type BatchFileState,
  type BatchSelection,
  type JobOutcome,
} from "@/lib/batch-upload";
import { CLAY, FONT, SAGE } from "@shared/brand";

const POLL_INTERVAL_MS = 2500;
const POLL_TIMEOUT_MS = 150_000;

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string).split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

type Phase = "upload" | "processing" | "summary" | "review";

interface BatchUploadModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientData: Client;
  /** Called after a successful Populate All so the dashboard can invalidate its caches. */
  onPopulated: () => void;
}

export function BatchUploadModal({ open, onOpenChange, clientData, onPopulated }: BatchUploadModalProps) {
  const clientId = clientData.id;
  const [phase, setPhase] = useState<Phase>("upload");
  const [files, setFiles] = useState<BatchFileState[]>([]);
  const [dragging, setDragging] = useState(false);
  const [populating, setPopulating] = useState(false);
  const [populatedCount, setPopulatedCount] = useState<number | null>(null);
  // 5B review (per-document stepper) state.
  const [reviewIndex, setReviewIndex] = useState(0);
  const [selection, setSelection] = useState<BatchSelection>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const counterRef = useRef(0);
  // Set once Populate All succeeds, so close() knows to refresh the dashboard. We
  // invalidate on CLOSE (not mid-populate): invalidating ["/api/clients"] while the
  // modal is open re-renders it shut before the "Data added ✓" confirmation can show.
  const didPopulateRef = useRef(false);

  // D-170.6: the three raw apiRequest writes in this file converted to the house pattern.
  // `extractMutation` fires per file inside the batch orchestrator, so it takes NO lock — the
  // batch legitimately runs one extraction per picked file. The populate lock below is what
  // guards the single Populate click.
  const populateFlight = useSingleFlight();
  const extractMutation = useMutation({
    mutationFn: async (v: { sectionId: string; body: Record<string, unknown> }) =>
      await apiRequest("POST", `/api/clients/${clientId}/sections/${v.sectionId}/extract-document`, v.body),
  });
  const populateSectionMutation = useMutation({
    mutationFn: async (v: { jobId: string; body: Record<string, unknown> }) =>
      await apiRequest("POST", `/api/extraction-jobs/${v.jobId}/populate-section`, v.body),
  });
  const homeDocumentMutation = useMutation({
    mutationFn: async (jobId: string) =>
      await apiRequest("POST", `/api/extraction-jobs/${jobId}/home-document`),
  });

  // Resolve the Important Documents section id (the batch "upload section" — its data
  // is staged for review; every other section fans out). Two cached queries.
  const { data: legalCategory } = useQuery<GenericCategory>({
    queryKey: ["/api/generic-categories/by-slug", CATEGORY_SLUGS.LEGAL, clientId],
    queryFn: async () => (await apiRequest("GET", `/api/generic-categories/by-slug/${CATEGORY_SLUGS.LEGAL}?clientId=${clientId}`)).json(),
    enabled: open,
  });
  const { data: legalSections } = useQuery<GenericSection[]>({
    queryKey: ["/api/generic-categories", legalCategory?.id, "sections"],
    enabled: open && !!legalCategory?.id,
  });
  const importantDocsSectionId = useMemo(
    () => legalSections?.find((s) => s.slug === SECTION_SLUGS.LEGAL_IMPORTANT_DOCUMENTS)?.id,
    [legalSections],
  );

  const patchFile = (id: string, patch: Partial<BatchFileState>) =>
    setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, ...patch } : f)));

  // ── Real deps for the orchestration ──────────────────────────────────────────
  const uploadAndExtract = async (file: File): Promise<string> => {
    if (!importantDocsSectionId) throw new Error("Section not ready");
    const base64 = await fileToBase64(file);
    const res = await extractMutation.mutateAsync({
      sectionId: importantDocsSectionId,
      body: {
        data: base64,
        contentType: file.type,
        filename: file.name,
        keepDocument: true, // preserve the R2 object so home-document can attach it durably
      },
    });
    const body = await res.json();
    if (!body?.jobId) throw new Error("Upload did not enqueue a job");
    return body.jobId as string;
  };

  const pollJobUntilDone = async (jobId: string): Promise<JobOutcome> => {
    const deadline = Date.now() + POLL_TIMEOUT_MS;
    while (Date.now() < deadline) {
      await sleep(POLL_INTERVAL_MS);
      try {
        const job = await (await apiRequest("GET", `/api/extraction-jobs/${jobId}`)).json();
        if (job.status === "completed") {
          return { status: "completed", documentType: job.result?.documentType, fanout: (job.result?.fanout ?? []) as FanOutSection[] };
        }
        if (job.status === "failed") return { status: "failed" };
      } catch {
        /* transient — keep polling */
      }
    }
    return { status: "failed" };
  };

  const startProcessing = async (picked: File[]) => {
    const next: Array<{ id: string; file: File; state: BatchFileState }> = picked.map((file) => {
      const id = `f${++counterRef.current}`;
      return { id, file, state: { id, name: file.name, status: "queued" } };
    });
    setFiles(next.map((n) => n.state));
    setPhase("processing");
    await runBatchProcessing(next.map((n) => ({ id: n.id, file: n.file })), { uploadAndExtract, pollJobUntilDone }, patchFile);
    setPhase("summary");
  };

  const onFilesChosen = (list: FileList | null) => {
    const picked = Array.from(list ?? []);
    if (picked.length > 0) void startProcessing(picked);
  };

  // Shared populate path for both 5A (Populate All) and 5B (Populate Selected): write the
  // given fan-out targets, then home each document into Important Documents (best-effort —
  // never blocks the confirmation). `rowIndexes` absent → all rows for that section.
  const finishPopulate = async (targets: Array<{ jobId: string; sectionId: string; rowIndexes?: number[] }>) => {
    // D-170.6: ONE lock over the whole populate operation, not one per iteration — this is a
    // single "Populate" click that fans out over N targets. Both loops below write real rows
    // (populate-section APPENDS with fresh rowIds; home-document appends an Important Documents
    // row), so a second click mid-run would double every record it has already written.
    const ran = await populateFlight.run(async () => {
      setPopulating(true);
      let ok = 0;
      for (const t of targets) {
        try {
          await populateSectionMutation.mutateAsync({
            jobId: t.jobId,
            body: t.rowIndexes ? { sectionId: t.sectionId, rowIndexes: t.rowIndexes } : { sectionId: t.sectionId },
          });
          ok++;
        } catch {
          /* skip this section; keep going */
        }
      }
      for (const f of files) {
        if (f.status === "success" && f.jobId) {
          try {
            await homeDocumentMutation.mutateAsync(f.jobId);
          } catch {
            /* non-fatal */
          }
        }
      }
      return ok;
    });
    if (ran === undefined) return; // duplicate click — the original run owns the summary
    const ok = ran;
    didPopulateRef.current = true;
    setPopulatedCount(ok);
    setPopulating(false);
    setPhase("summary"); // 5B ends back on the summary layout, now showing the confirmation
  };

  const handlePopulateAll = () => finishPopulate(collectPopulateTargets(files));
  const handlePopulateSelected = () => finishPopulate(collectSelectedPopulateTargets(files, selection));

  // ── 5B review stepper ─────────────────────────────────────────────────────────
  const successFiles = useMemo(() => files.filter((f) => f.status === "success"), [files]);
  const enterReview = () => {
    setSelection(initBatchSelection(files));
    setReviewIndex(0);
    setPhase("review");
  };
  const toggleSection = (fileId: string, sectionId: string) =>
    setSelection((prev) => {
      const file = successFiles.find((f) => f.id === fileId);
      const rowCount = file?.sections?.find((s) => s.sectionId === sectionId)?.rows.length ?? 0;
      const cur = prev[fileId]?.[sectionId] ?? [];
      const next = cur.length === rowCount ? [] : Array.from({ length: rowCount }, (_, i) => i);
      return { ...prev, [fileId]: { ...prev[fileId], [sectionId]: next } };
    });
  const toggleRow = (fileId: string, sectionId: string, rowIndex: number) =>
    setSelection((prev) => {
      const cur = prev[fileId]?.[sectionId] ?? [];
      const next = cur.includes(rowIndex) ? cur.filter((i) => i !== rowIndex) : [...cur, rowIndex];
      return { ...prev, [fileId]: { ...prev[fileId], [sectionId]: next } };
    });

  const reset = () => {
    setPhase("upload");
    setFiles([]);
    setPopulating(false);
    setPopulatedCount(null);
    setReviewIndex(0);
    setSelection({});
    didPopulateRef.current = false;
  };
  const close = () => {
    // Refresh the dashboard (card dismiss, essentials, category progress) on the way
    // out — only when a populate actually happened, so a plain Cancel doesn't refetch.
    if (didPopulateRef.current) onPopulated();
    onOpenChange(false);
    // defer reset so the closing animation doesn't flash the upload state
    setTimeout(reset, 250);
  };

  const successCount = files.filter((f) => f.status === "success").length;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent
        className="sm:max-w-2xl max-h-[90vh] overflow-y-auto"
        // Don't let an outside click / Escape dismiss mid-analysis — a dropped batch
        // would keep processing invisibly. The footer Cancel (summary state) is the exit.
        onInteractOutside={(e) => { if (phase === "processing") e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (phase === "processing") e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle style={{ fontFamily: FONT.serif }}>Build from your documents</DialogTitle>
          <DialogDescription>
            Upload your will, policies, statements, or leases. We'll read each one and fill in the
            matching sections — one document can populate up to 12.
          </DialogDescription>
        </DialogHeader>

        {/* ── State 1: upload ─────────────────────────────────────────────── */}
        {phase === "upload" && (
          <div
            className="rounded-xl border-2 border-dashed px-6 py-12 text-center transition-colors"
            style={{ borderColor: dragging ? SAGE[600] : "#D8D2C4", backgroundColor: dragging ? SAGE[50] : undefined }}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => { e.preventDefault(); setDragging(false); onFilesChosen(e.dataTransfer.files); }}
            data-testid="batch-upload-dropzone"
          >
            <div className="mx-auto rounded-full p-3 w-fit" style={{ backgroundColor: SAGE[50] }}>
              <UploadCloud className="h-7 w-7" style={{ color: SAGE[600] }} />
            </div>
            <p className="mt-3 font-medium">Drag files here, or click to browse</p>
            <p className="text-sm text-muted-foreground mt-1">{DOCUMENT_ACCEPT_LABEL} — add as many as you like</p>
            <input
              ref={fileInputRef}
              type="file"
              accept={DOCUMENT_ACCEPT}
              multiple
              className="hidden"
              onChange={(e) => onFilesChosen(e.target.files)}
              data-testid="batch-upload-input"
            />
            <Button className="mt-4" onClick={() => fileInputRef.current?.click()} data-testid="button-batch-choose-files">
              Choose files
            </Button>
          </div>
        )}

        {/* ── State 2/3: file list (processing + summary share the row layout) ── */}
        {(phase === "processing" || phase === "summary") && (
          <div className="space-y-2" data-testid="batch-file-list">
            {files.map((f) => (
              <div key={f.id} className="flex items-start gap-3 rounded-lg border px-3 py-2.5" data-testid={`batch-file-${f.id}`}>
                <FileText className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{f.name}</p>
                  <p className="text-xs mt-0.5 flex items-center gap-1.5">
                    {f.status === "queued" && <span className="text-muted-foreground">Queued</span>}
                    {f.status === "processing" && (
                      <span className="flex items-center gap-1.5" style={{ color: SAGE[600] }}>
                        <Loader2 className="h-3 w-3 animate-spin" /> Analyzing…
                      </span>
                    )}
                    {f.status === "success" && (
                      <span className="flex items-center gap-1.5" style={{ color: SAGE[600] }}>
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        {documentTypeLabel(f.documentType)} detected — {f.sections?.length ?? 0} section
                        {(f.sections?.length ?? 0) !== 1 ? "s" : ""}
                      </span>
                    )}
                    {f.status === "unclassified" && (
                      <span className="flex items-center gap-1.5" style={{ color: CLAY[600] }}>
                        <AlertTriangle className="h-3.5 w-3.5" /> Couldn't identify this document
                      </span>
                    )}
                    {f.status === "failed" && (
                      <span className="flex items-center gap-1.5" style={{ color: CLAY[600] }}>
                        <AlertTriangle className="h-3.5 w-3.5" /> Analysis failed
                      </span>
                    )}
                  </p>
                </div>
              </div>
            ))}

            {phase === "summary" && populatedCount === null && (
              <p className="text-sm text-muted-foreground pt-1">
                {successCount > 0
                  ? `Ready to add ${totalSections(files)} section${totalSections(files) !== 1 ? "s" : ""} across ${successCount} document${successCount !== 1 ? "s" : ""}.`
                  : "We couldn't read any of these documents. You can try different files."}
              </p>
            )}

            {populatedCount !== null && (
              <div className="flex items-center gap-2 rounded-lg px-4 py-3 mt-1" style={{ backgroundColor: SAGE[50], color: SAGE[600] }} data-testid="batch-populated-confirm">
                <Check className="h-4 w-4" />
                <span className="text-sm font-medium">Data added to {populatedCount} section{populatedCount !== 1 ? "s" : ""}.</span>
              </div>
            )}
          </div>
        )}

        {/* ── State 4: per-document review stepper (5B) ───────────────────── */}
        {phase === "review" && successFiles[reviewIndex] && (() => {
          const doc = successFiles[reviewIndex];
          return (
            <div className="space-y-3" data-testid="batch-review">
              <div className="flex items-baseline justify-between">
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{doc.name}</p>
                  <p className="text-xs text-muted-foreground">{documentTypeLabel(doc.documentType)} · uncheck anything you don't want to add</p>
                </div>
                <span className="text-xs text-muted-foreground shrink-0">Document {reviewIndex + 1} of {successFiles.length}</span>
              </div>
              <FanOutSectionChecklist
                sections={doc.sections ?? []}
                selected={selection[doc.id] ?? {}}
                onToggleSection={(sectionId) => toggleSection(doc.id, sectionId)}
                onToggleRow={(sectionId, rowIndex) => toggleRow(doc.id, sectionId, rowIndex)}
              />
            </div>
          );
        })()}

        <DialogFooter className="gap-2 sm:gap-0">
          {phase === "processing" && (
            <Button variant="ghost" disabled data-testid="button-batch-processing">
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> Analyzing your documents…
            </Button>
          )}
          {phase === "summary" && populatedCount === null && (
            <>
              <Button variant="ghost" onClick={close} data-testid="button-batch-cancel">Cancel</Button>
              <Button variant="outline" onClick={enterReview} disabled={successCount === 0} data-testid="button-batch-review-choose">
                Review &amp; choose
              </Button>
              <Button onClick={handlePopulateAll} disabled={populating || successCount === 0} data-testid="button-batch-populate-all">
                {populating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Populate all
              </Button>
            </>
          )}
          {phase === "review" && (
            <>
              <Button
                variant="ghost"
                onClick={() => (reviewIndex === 0 ? setPhase("summary") : setReviewIndex((i) => i - 1))}
                data-testid="button-batch-review-back"
              >
                {reviewIndex === 0 ? "Back" : <><ArrowLeft className="h-4 w-4" aria-hidden="true" />Previous</>}
              </Button>
              {reviewIndex < successFiles.length - 1 ? (
                <Button onClick={() => setReviewIndex((i) => i + 1)} data-testid="button-batch-review-next">
                  Next document <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Button>
              ) : (
                <Button onClick={handlePopulateSelected} disabled={populating} data-testid="button-batch-populate-selected">
                  {populating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Populate selected
                </Button>
              )}
            </>
          )}
          {populatedCount !== null && (
            <Button onClick={close} data-testid="button-batch-done">Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
